import React, { useState, useEffect, useRef, useCallback } from 'react';
import WaveLines from './components/WaveLines';
import './App.css';

/**
 * API base for initiating/verifying demo sessions.
 *
 * - In local dev, prefer relative `/api/...` so CRA's `proxy` (package.json) routes to localhost:8082.
 * - In production builds (static hosting), set REACT_APP_API_BASE_URL (e.g. https://api.veritage.io).
 */
const API_BASE = process.env.REACT_APP_API_BASE_URL ?? '';
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/veritage-logo-color.svg`;
/** Base URL for verify app (iframe + footer links). Use REACT_APP_VERIFY_BASE_URL for local dev (e.g. http://localhost:3001). */
const VERIFY_BASE = process.env.REACT_APP_VERIFY_BASE_URL ?? 'https://verify.veritage.io';
const VERIFY_LEGAL_BASE = VERIFY_BASE;

const IS_NGROK_API = (() => {
  try {
    return API_BASE && /\.ngrok-free\.app$/i.test(new URL(API_BASE).hostname);
  } catch (_) {
    return false;
  }
})();

// Simple icons (no MUI dependency)
const IconShield = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const IconBadge = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
  </svg>
);
const IconKey = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);
const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
);
const IconTimer = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
);
const IconVerified = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
);
const IconArrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
);

/**
 * Demotwo: flow picker is the main screen (verify’s first screen). When a flow is
 * chosen, the verify app loads in an iframe with ?flow= so it starts on that
 * flow’s consent form. Navbar stays visible above the iframe.
 */
function App() {
  const [verificationUrl, setVerificationUrl] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [, setVerificationData] = useState(null);
  const iframeRef = useRef(null);

  /** Open a flow: initiate session when user clicks (same as test harness). */
  const openFlow = useCallback((flow) => {
    setVerificationError(null);
    setIsVerifying(true);
    const flowParam = flow ? `&flow=${encodeURIComponent(flow)}` : '';
    fetch(`${API_BASE}/api/age-gate/initiate-iframe?t=${Date.now()}${flowParam}`, {
      method: 'GET',
      headers: IS_NGROK_API ? { 'ngrok-skip-browser-warning': '1' } : undefined,
    })
      .then((res) => {
        if (!res.ok) return res.text().then((t) => Promise.reject(new Error(t || 'Failed to initiate')));
        return res.json();
      })
      .then((data) => {
        if (!data || !data.verificationUrl) return;
        let url = data.verificationUrl;
        if (process.env.REACT_APP_VERIFY_BASE_URL) {
          const base = process.env.REACT_APP_VERIFY_BASE_URL.replace(/\/$/, '');
          url = url.replace(/^https?:\/\/[^/]+/, base);
        }
        setVerificationUrl(url);
        setSelectedFlow(flow);
      })
      .catch((err) => {
        setVerificationError(err.message || 'Failed to load verification.');
      })
      .finally(() => {
        setIsVerifying(false);
      });
  }, []);

  const verifyToken = useCallback(async (token) => {
    setIsVerifying(true);
    setVerificationError(null);
    try {
      const response = await fetch(`${API_BASE}/api/age-gate/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(IS_NGROK_API ? { 'ngrok-skip-browser-warning': '1' } : {}),
        },
        body: JSON.stringify({ token }),
      });
      const data = await response.json();
      if (data.verified) {
        setVerificationData(data);
        setSelectedFlow(null);
        /* Don't set isVerified - go straight back to flow picker (home) */
      } else {
        setVerificationError(data.error || 'Verification failed');
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      setVerificationError('Failed to verify token. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      const rawVerifyBase = (process.env.REACT_APP_VERIFY_BASE_URL ?? 'https://verify.veritage.io').replace(/\/$/, '');
      let verifyOrigin = rawVerifyBase;
      try {
        verifyOrigin = new URL(rawVerifyBase).origin;
      } catch (_) {}

      // Be explicit for production (showcase embeds verify) + keep local dev.
      const allowedOrigins = [
        // Local dev
        'http://localhost:3001',
        'http://127.0.0.1:3001',

        // Production defaults
        'https://verify.veritage.io',
        'https://showcase.veritage.io',

        // Config-driven + current origin
        verifyOrigin,
        window.location.origin,
      ];
      if (!allowedOrigins.includes(event.origin)) return;
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'veritage-verification-complete') {
        const { verificationToken } = event.data;
        if (verificationToken) {
          setSelectedFlow(null);
          verifyToken(verificationToken);
        }
      } else if (event.data.type === 'veritage-verification-failed') {
        setSelectedFlow(null);
        setVerificationError(event.data.error || 'Verification failed');
        setIsVerifying(false);
      } else if (event.data.type === 'veritage-verification-error') {
        setSelectedFlow(null);
        setVerificationError(event.data.error || 'Verification error occurred');
        setIsVerifying(false);
      } else if (event.data.type === 'veritage-verification-back') {
        setSelectedFlow(null);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [verifyToken]);

  const iframeSrc = selectedFlow && verificationUrl ? verificationUrl : null;

  const renderNavbar = () => (
    <header className="vt-nav demotwo-nav">
      <a className="demotwo-brand" href="https://veritage.io" aria-label="Veritage">
        <img src={LOGO_SRC} alt="Veritage" className="demotwo-brand-logo" />
        <span className="demotwo-brand-divider" aria-hidden="true" />
        <span className="demotwo-brand-title">Age Playground</span>
      </a>
      <nav className="demotwo-nav-links" aria-label="Veritage">
        <a className="vt-nav-link" href="https://docs.veritage.io">Docs</a>
      </nav>
      <a className="vt-btn vt-btn--sm demotwo-nav-cta" href="https://app.veritage.io">
        Get API keys
        <span className="vt-btn-arrow"><IconArrow /></span>
      </a>
    </header>
  );

  const renderShell = (children) => (
    <div className="demotwo-layout">
      <div className="demotwo-backdrop" aria-hidden="true">
        <WaveLines className="demotwo-waves" lines={34} amplitude={120} opacity={0.6} animate />
      </div>
      {renderNavbar()}
      {children}
    </div>
  );

  // Loading: navbar + spinner
  if (isVerifying && !verificationUrl) {
    return renderShell(
      <main className="demotwo-main demotwo-main--center">
        <div className="demotwo-state-card">
          <div className="demotwo-spinner" />
          <p className="demotwo-screen-text">Starting verification…</p>
        </div>
      </main>
    );
  }

  // Init failed
  if (verificationError && !verificationUrl) {
    return renderShell(
      <main className="demotwo-main demotwo-main--center">
        <div className="demotwo-state-card">
          <div className="demotwo-state-icon demotwo-state-icon--error" aria-hidden="true">!</div>
          <h2 className="demotwo-state-title">Verification didn't start</h2>
          <p className="demotwo-screen-text demotwo-screen-text--error">{verificationError}</p>
          <button type="button" className="vt-btn vt-btn--sm" onClick={() => setVerificationError(null)}>
            Try again <span className="vt-btn-arrow"><IconArrow /></span>
          </button>
        </div>
      </main>
    );
  }

  // Success
  if (isVerified) {
    return renderShell(
      <main className="demotwo-main demotwo-main--center">
        <div className="demotwo-state-card">
          <div className="demotwo-state-icon demotwo-state-icon--success" aria-hidden="true"><IconVerified /></div>
          <h2 className="demotwo-state-title">Age verified</h2>
          <button
            type="button"
            className="vt-btn vt-btn--soft vt-btn--sm"
            onClick={() => {
              setIsVerified(false);
              setVerificationData(null);
              window.location.reload();
            }}
          >
            Verify again
          </button>
        </div>
      </main>
    );
  }

  const methods = [
    {
      flow: 'age-estimation', icon: <IconShield />, title: 'Age estimation', cta: 'Continue',
      desc: 'Use your camera to estimate age, no identity required.',
      features: [[<IconTimer />, 'Fast, low-friction check'], [<IconShield />, 'No one sees, stores, or shares your image'], [<IconLock />, 'Secure processing']],
    },
    {
      flow: 'id-verification', icon: <IconBadge />, title: 'ID verification', cta: 'Continue',
      desc: 'Use a passport, driving licence, or national ID.',
      features: [[<IconBadge />, 'Capture or upload your document'], [<IconVerified />, 'Optional face match to prevent misuse'], [<IconShield />, 'No long-term storage of your document or selfie']],
    },
    {
      flow: 'passkey', icon: <IconKey />, title: 'Veritage Key', cta: 'Use Veritage Key',
      desc: 'Reuse a previous verification with a passkey.',
      features: [[<IconKey />, 'Reuse a previous age verification'], [<IconLock />, 'Secured by your device passkey'], [<IconTimer />, 'One tap, no re-upload']],
    },
  ];

  // Flow picker (always visible); when a flow is selected, show iframe in modal on top
  return renderShell(
    <>
      <main className="demotwo-main">
        <section className="demotwo-hero">
          <h1 className="demotwo-title">Choose how you want to <span className="demotwo-accent">confirm your age</span></h1>
          <p className="demotwo-lead">Three ways to prove you're old enough, from a quick selfie estimate to a reusable passkey. Ask for less, know enough.</p>
        </section>
        <section className="demotwo-grid" aria-label="Age confirmation methods">
          {methods.map((m) => (
            <article className="demotwo-card" key={m.flow}>
              <div className="demotwo-card-head">
                <div className="demotwo-icon-tile" aria-hidden="true">{m.icon}</div>
                <h2 className="demotwo-card-title">{m.title}</h2>
                <p className="demotwo-card-desc">{m.desc}</p>
              </div>
              <ul className="demotwo-feature-list">
                {m.features.map(([icon, text]) => (
                  <li key={text}><span className="demotwo-feature-icon">{icon}</span><span>{text}</span></li>
                ))}
              </ul>
              <div className="demotwo-card-actions">
                <button type="button" className="vt-btn demotwo-card-btn" onClick={() => openFlow(m.flow)}>
                  {m.cta}
                  <span className="vt-btn-arrow"><IconArrow /></span>
                </button>
              </div>
            </article>
          ))}
        </section>
      </main>

      <footer className="demotwo-footer">
        <div className="demotwo-footer-row">
          <img src={LOGO_SRC} alt="Veritage" className="demotwo-footer-logo" />
          <div className="demotwo-footer-compliance" aria-label="Compliance">
            <span className="demotwo-footer-badge">ISO/IEC 27001</span>
            <span className="demotwo-footer-badge">GDPR</span>
            <span className="demotwo-footer-badge">SOC 2 Type II</span>
          </div>
          <div className="demotwo-footer-links" aria-label="Legal links">
            <a className="demotwo-footer-link" href={`${VERIFY_LEGAL_BASE}/privacy`} target="_blank" rel="noopener noreferrer">Privacy</a>
            <a className="demotwo-footer-link" href={`${VERIFY_LEGAL_BASE}/terms`} target="_blank" rel="noopener noreferrer">Terms</a>
            <a className="demotwo-footer-link" href={`${VERIFY_LEGAL_BASE}/security`} target="_blank" rel="noopener noreferrer">Security</a>
          </div>
        </div>
        <p className="demotwo-footer-copyright">© {new Date().getFullYear()} Veritage. All rights reserved.</p>
      </footer>

      {/* Modal: dimmed backdrop + centered iframe (same pattern as test harness) */}
      {iframeSrc && (
        <>
          <div
            className="demotwo-iframe-backdrop"
            aria-hidden="true"
            onClick={() => setSelectedFlow(null)}
          />
          <div className="demotwo-iframe-embed-wrapper">
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              title="Veritage Age Verification"
              className="demotwo-iframe-embed-iframe"
              allow="camera; microphone; geolocation; publickey-credentials-get; publickey-credentials-create; digital-credentials-get"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-top-navigation-by-user-activation"
            />
          </div>
        </>
      )}
    </>
  );
}

export default App;
